import { renderCanvas } from "./canvas-surface";
/** Pixel algorithms copied from the original editor; no host or Fabric dependencies. */
let reliableCanvasBlurFilters;
function getImageBlurPixels(value, width, height) {
  const amount = Math.max(0, Math.min(100, Number(value) || 0)) / 100;
  if (!amount) return 0;
  const maximum = Math.min(
    180,
    Math.max(36, Math.min(Math.max(1, width), Math.max(1, height)) * 0.32),
  );
  return Math.max(0.5, Math.pow(amount, 1.35) * maximum);
}

function supportsReliableCanvasBlurFilters() {
  if (typeof reliableCanvasBlurFilters === "boolean")
    return reliableCanvasBlurFilters;
  try {
    const source = renderCanvas();
    source.width = 5;
    source.height = 1;
    const sourceContext = source.getContext("2d");
    sourceContext.fillStyle = "#ffffff";
    sourceContext.fillRect(2, 0, 1, 1);
    const output = renderCanvas();
    output.width = 5;
    output.height = 1;
    const outputContext = output.getContext("2d");
    outputContext.filter = "blur(2px)";
    outputContext.drawImage(source, 0, 0);
    const pixels = outputContext.getImageData(0, 0, 5, 1).data;
    reliableCanvasBlurFilters = pixels[7] > 0 || pixels[15] > 0;
  } catch {
    reliableCanvasBlurFilters = false;
  }
  return reliableCanvasBlurFilters;
}

function applyHueSaturationPixelFallback(
  context,
  width,
  height,
  hue,
  saturation,
) {
  const safeHue = Math.max(-180, Math.min(180, Number(hue) || 0));
  const safeSaturation = Math.max(-100, Math.min(100, Number(saturation) || 0));
  if (!safeHue && !safeSaturation) return;
  try {
    const imageData = context.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    const angle = (safeHue * Math.PI) / 180;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const saturationFactor = Math.max(0, 1 + safeSaturation / 100);
    for (let index = 0; index < pixels.length; index += 4) {
      const red = pixels[index];
      const green = pixels[index + 1];
      const blue = pixels[index + 2];
      const hueRed =
        (0.213 + cosine * 0.787 - sine * 0.213) * red +
        (0.715 - cosine * 0.715 - sine * 0.715) * green +
        (0.072 - cosine * 0.072 + sine * 0.928) * blue;
      const hueGreen =
        (0.213 - cosine * 0.213 + sine * 0.143) * red +
        (0.715 + cosine * 0.285 + sine * 0.14) * green +
        (0.072 - cosine * 0.072 - sine * 0.283) * blue;
      const hueBlue =
        (0.213 - cosine * 0.213 - sine * 0.787) * red +
        (0.715 - cosine * 0.715 + sine * 0.715) * green +
        (0.072 + cosine * 0.928 + sine * 0.072) * blue;
      const luminance = hueRed * 0.213 + hueGreen * 0.715 + hueBlue * 0.072;
      pixels[index] = Math.max(
        0,
        Math.min(255, luminance + (hueRed - luminance) * saturationFactor),
      );
      pixels[index + 1] = Math.max(
        0,
        Math.min(255, luminance + (hueGreen - luminance) * saturationFactor),
      );
      pixels[index + 2] = Math.max(
        0,
        Math.min(255, luminance + (hueBlue - luminance) * saturationFactor),
      );
    }
    context.putImageData(imageData, 0, 0);
  } catch {
    // Cross-origin images may prevent pixel access; the original image remains usable.
  }
}

function normalizeImageBackgroundRemovalColor(value) {
  const color = String(value || "")
    .trim()
    .toLowerCase();
  return /^#[0-9a-f]{6}$/.test(color) ? color : "";
}

function normalizeImageBackgroundRemovalMode(value) {
  return value === "edge" ? "edge" : "all";
}

function hexToImageBackgroundRgb(value) {
  const color = normalizeImageBackgroundRemovalColor(value) || "#ffffff";
  return {
    r: parseInt(color.slice(1, 3), 16),
    g: parseInt(color.slice(3, 5), 16),
    b: parseInt(color.slice(5, 7), 16),
  };
}

function sampleImageBackgroundPatch(pixels, width, height, x, y, radius) {
  const left = Math.max(0, Math.round(x) - radius);
  const right = Math.min(width - 1, Math.round(x) + radius);
  const top = Math.max(0, Math.round(y) - radius);
  const bottom = Math.min(height - 1, Math.round(y) + radius);
  let red = 0;
  let green = 0;
  let blue = 0;
  let weight = 0;
  for (let row = top; row <= bottom; row += 1) {
    for (let column = left; column <= right; column += 1) {
      const offset = (row * width + column) * 4;
      const alpha = pixels[offset + 3] / 255;
      if (alpha < 0.08) continue;
      red += pixels[offset] * alpha;
      green += pixels[offset + 1] * alpha;
      blue += pixels[offset + 2] * alpha;
      weight += alpha;
    }
  }
  if (!weight) return null;
  return { r: red / weight, g: green / weight, b: blue / weight };
}

function imageBackgroundColorDistance(red, green, blue, reference) {
  const redDifference = red - reference.r;
  const greenDifference = green - reference.g;
  const blueDifference = blue - reference.b;
  // RMS distance keeps the sensitivity scale intuitive (0–255), while
  // still reacting to a tint in any individual colour channel.
  return Math.sqrt(
    (redDifference * redDifference +
      greenDifference * greenDifference +
      blueDifference * blueDifference) /
      3,
  );
}

function buildAutomaticImageBackgroundModel(imageData) {
  const { width, height, data } = imageData;
  const insetX = Math.min(width - 1, Math.max(0, Math.round(width * 0.012)));
  const insetY = Math.min(height - 1, Math.max(0, Math.round(height * 0.012)));
  const patchRadius = Math.max(
    1,
    Math.min(6, Math.round(Math.min(width, height) * 0.006)),
  );
  const cornerPoints = [
    [insetX, insetY],
    [width - 1 - insetX, insetY],
    [width - 1 - insetX, height - 1 - insetY],
    [insetX, height - 1 - insetY],
  ];
  const fallback = { r: 255, g: 255, b: 255 };
  const corners = cornerPoints.map(([x, y]) =>
    sampleImageBackgroundPatch(data, width, height, x, y, patchRadius),
  );
  const availableCorners = corners.filter(Boolean);
  const backgroundCorners = availableCorners.length
    ? availableCorners.reduce((best, candidate) => {
        const cluster = availableCorners.filter(
          (color) =>
            imageBackgroundColorDistance(
              color.r,
              color.g,
              color.b,
              candidate,
            ) <= 72,
        );
        return cluster.length > best.length ? cluster : best;
      }, [])
    : [];
  const averageCorner = backgroundCorners.length
    ? backgroundCorners.reduce(
        (result, color) => ({
          r: result.r + color.r / backgroundCorners.length,
          g: result.g + color.g / backgroundCorners.length,
          b: result.b + color.b / backgroundCorners.length,
        }),
        { r: 0, g: 0, b: 0 },
      )
    : fallback;
  const safeCorners = corners.map((color) =>
    color &&
    imageBackgroundColorDistance(color.r, color.g, color.b, averageCorner) <= 72
      ? color
      : averageCorner,
  );
  const edgePoints = [
    [width * 0.25, insetY],
    [width * 0.5, insetY],
    [width * 0.75, insetY],
    [width - 1 - insetX, height * 0.25],
    [width - 1 - insetX, height * 0.5],
    [width - 1 - insetX, height * 0.75],
    [width * 0.75, height - 1 - insetY],
    [width * 0.5, height - 1 - insetY],
    [width * 0.25, height - 1 - insetY],
    [insetX, height * 0.75],
    [insetX, height * 0.5],
    [insetX, height * 0.25],
  ];
  const samples = safeCorners.slice();
  edgePoints.forEach(([x, y]) => {
    const sample = sampleImageBackgroundPatch(
      data,
      width,
      height,
      x,
      y,
      patchRadius,
    );
    if (!sample) return;
    // Ignore an edge sample that is clearly a foreground object touching
    // the image boundary; mild gradients remain part of the model.
    const closeToCorner = safeCorners.some(
      (color) =>
        imageBackgroundColorDistance(sample.r, sample.g, sample.b, color) <= 72,
    );
    if (closeToCorner) samples.push(sample);
  });
  return { corners: safeCorners, samples, average: averageCorner };
}

function getAutomaticImageBackgroundReference(model, x, y, width, height) {
  const u = width > 1 ? x / (width - 1) : 0;
  const v = height > 1 ? y / (height - 1) : 0;
  const [topLeft, topRight, bottomRight, bottomLeft] = model.corners;
  return {
    r:
      topLeft.r * (1 - u) * (1 - v) +
      topRight.r * u * (1 - v) +
      bottomRight.r * u * v +
      bottomLeft.r * (1 - u) * v,
    g:
      topLeft.g * (1 - u) * (1 - v) +
      topRight.g * u * (1 - v) +
      bottomRight.g * u * v +
      bottomLeft.g * (1 - u) * v,
    b:
      topLeft.b * (1 - u) * (1 - v) +
      topRight.b * u * (1 - v) +
      bottomRight.b * u * v +
      bottomLeft.b * (1 - u) * v,
  };
}

function applyImageBackgroundRemovalToCanvas(canvasElement, settings = {}) {
  if (!settings.enabled) return false;
  const context = canvasElement.getContext("2d", { willReadFrequently: true });
  if (!context) return false;

  try {
    const width = canvasElement.width;
    const height = canvasElement.height;
    const imageData = context.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    const automaticModel = buildAutomaticImageBackgroundModel(imageData);
    const manualColor = normalizeImageBackgroundRemovalColor(settings.color);
    const manualReference = manualColor
      ? hexToImageBackgroundRgb(manualColor)
      : null;
    const sensitivity = Math.max(
      0,
      Math.min(100, Number(settings.sensitivity) || 0),
    );
    const softness = Math.max(0, Math.min(100, Number(settings.softness) || 0));
    const threshold = 4 + sensitivity * 0.92;
    const feather = softness * 0.42;
    const candidateLimit = threshold + feather;
    const mode = normalizeImageBackgroundRemovalMode(settings.mode);
    const pixelCount = width * height;
    const keepFactors = new Uint8Array(pixelCount);

    for (let index = 0; index < pixelCount; index += 1) {
      const offset = index * 4;
      if (!pixels[offset + 3]) {
        keepFactors[index] = 0;
        continue;
      }
      const x = index % width;
      const y = Math.floor(index / width);
      let distance;
      if (manualReference) {
        distance = imageBackgroundColorDistance(
          pixels[offset],
          pixels[offset + 1],
          pixels[offset + 2],
          manualReference,
        );
      } else {
        const gradientReference = getAutomaticImageBackgroundReference(
          automaticModel,
          x,
          y,
          width,
          height,
        );
        distance = imageBackgroundColorDistance(
          pixels[offset],
          pixels[offset + 1],
          pixels[offset + 2],
          gradientReference,
        );
        if (distance > threshold) {
          for (
            let sampleIndex = 0;
            sampleIndex < automaticModel.samples.length;
            sampleIndex += 1
          ) {
            const reference = automaticModel.samples[sampleIndex];
            distance = Math.min(
              distance,
              imageBackgroundColorDistance(
                pixels[offset],
                pixels[offset + 1],
                pixels[offset + 2],
                reference,
              ),
            );
            if (distance <= threshold) break;
          }
        }
      }

      if (distance <= threshold) {
        keepFactors[index] = 0;
      } else if (!feather || distance >= candidateLimit) {
        keepFactors[index] = 255;
      } else {
        const ratio = (distance - threshold) / feather;
        const smoothRatio = ratio * ratio * (3 - 2 * ratio);
        keepFactors[index] = Math.round(smoothRatio * 255);
      }
    }

    if (mode === "all") {
      for (let index = 0; index < pixelCount; index += 1) {
        const alphaOffset = index * 4 + 3;
        pixels[alphaOffset] = Math.round(
          (pixels[alphaOffset] * keepFactors[index]) / 255,
        );
      }
    } else {
      const visited = new Uint8Array(pixelCount);
      const queue = new Int32Array(pixelCount);
      let head = 0;
      let tail = 0;
      const enqueue = (index) => {
        if (visited[index] || keepFactors[index] === 255) return;
        visited[index] = 1;
        queue[tail++] = index;
      };
      for (let x = 0; x < width; x += 1) {
        enqueue(x);
        enqueue((height - 1) * width + x);
      }
      for (let y = 1; y < height - 1; y += 1) {
        enqueue(y * width);
        enqueue(y * width + width - 1);
      }
      while (head < tail) {
        const index = queue[head++];
        const x = index % width;
        if (x > 0) enqueue(index - 1);
        if (x < width - 1) enqueue(index + 1);
        if (index >= width) enqueue(index - width);
        if (index < pixelCount - width) enqueue(index + width);
      }
      for (let index = 0; index < pixelCount; index += 1) {
        if (!visited[index]) continue;
        const alphaOffset = index * 4 + 3;
        pixels[alphaOffset] = Math.round(
          (pixels[alphaOffset] * keepFactors[index]) / 255,
        );
      }
    }

    context.putImageData(imageData, 0, 0);
    return true;
  } catch {
    return false;
  }
}

function boxBlurImageData(imageData, radius) {
  const width = imageData.width;
  const height = imageData.height;
  const safeRadius = Math.max(1, Math.round(radius));
  const size = width * height * 4;
  let source = new Float32Array(size);
  for (let index = 0; index < size; index += 4) {
    const alpha = imageData.data[index + 3] / 255;
    source[index] = imageData.data[index] * alpha;
    source[index + 1] = imageData.data[index + 1] * alpha;
    source[index + 2] = imageData.data[index + 2] * alpha;
    source[index + 3] = imageData.data[index + 3];
  }

  const blurPass = (input, horizontal) => {
    const output = new Float32Array(size);
    const lineCount = horizontal ? height : width;
    const lineLength = horizontal ? width : height;
    const windowSize = safeRadius * 2 + 1;
    for (let line = 0; line < lineCount; line++) {
      const sums = [0, 0, 0, 0];
      const offset = (position) =>
        horizontal
          ? (line * width + position) * 4
          : (position * width + line) * 4;
      for (let position = -safeRadius; position <= safeRadius; position++) {
        const sampleOffset = offset(
          Math.max(0, Math.min(lineLength - 1, position)),
        );
        for (let channel = 0; channel < 4; channel++)
          sums[channel] += input[sampleOffset + channel];
      }
      for (let position = 0; position < lineLength; position++) {
        const targetOffset = offset(position);
        for (let channel = 0; channel < 4; channel++)
          output[targetOffset + channel] = sums[channel] / windowSize;
        const removeOffset = offset(Math.max(0, position - safeRadius));
        const addOffset = offset(
          Math.min(lineLength - 1, position + safeRadius + 1),
        );
        for (let channel = 0; channel < 4; channel++) {
          sums[channel] +=
            input[addOffset + channel] - input[removeOffset + channel];
        }
      }
    }
    return output;
  };

  for (let pass = 0; pass < 2; pass++) {
    source = blurPass(source, true);
    source = blurPass(source, false);
  }
  for (let index = 0; index < size; index += 4) {
    const alpha = Math.max(0, Math.min(255, source[index + 3]));
    const divisor = alpha > 0 ? alpha / 255 : 1;
    imageData.data[index] = Math.max(0, Math.min(255, source[index] / divisor));
    imageData.data[index + 1] = Math.max(
      0,
      Math.min(255, source[index + 1] / divisor),
    );
    imageData.data[index + 2] = Math.max(
      0,
      Math.min(255, source[index + 2] / divisor),
    );
    imageData.data[index + 3] = alpha;
  }
  return imageData;
}

function applyImageEdgesToCanvas(surface, settings) {
  if (!settings || settings.style === "none") return surface;
  const w = surface.width,
    h = surface.height;
  const depth = Math.max(
    1,
    Math.min(w, h) * (0.012 + settings.amount * 0.0016),
  );
  const mask = renderCanvas();
  mask.width = w;
  mask.height = h;
  const ctx = mask.getContext("2d");
  ctx.fillStyle = "#fff";
  if (settings.style === "blur") {
    // Blur the mask only: details in the centre stay sharp.
    const blur = depth / 2;
    if (supportsReliableCanvasBlurFilters()) ctx.filter = `blur(${blur}px)`;
    ctx.fillRect(
      depth,
      depth,
      Math.max(1, w - 2 * depth),
      Math.max(1, h - 2 * depth),
    );
    ctx.filter = "none";
    if (!supportsReliableCanvasBlurFilters()) {
      ctx.putImageData(
        boxBlurImageData(
          ctx.getImageData(0, 0, w, h),
          Math.max(1, blur * 0.52),
        ),
        0,
        0,
      );
    }
  } else if (settings.style === "stamp") {
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "destination-out";
    const radius = depth * 0.5;
    for (const [length, vertical] of [
      [w, false],
      [h, true],
    ]) {
      const count = Math.max(3, Math.round(length / (radius * 3)));
      for (let i = 0; i <= count; i++) {
        const p = (length * i) / count;
        for (const opposite of [false, true]) {
          ctx.beginPath();
          ctx.arc(
            vertical ? (opposite ? w : 0) : p,
            vertical ? p : opposite ? h : 0,
            radius,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
      }
    }
  } else {
    ctx.beginPath();
    const edges = [
      [w, (p, d) => [p, d]],
      [h, (p, d) => [w - d, p]],
      [w, (p, d) => [w - p, h - d]],
      [h, (p, d) => [d, h - p]],
    ];
    edges.forEach(([length, point], edge) => {
      const teeth = Math.max(4, Math.round(length / (depth * 2.6)));
      const count =
        settings.style === "wave"
          ? teeth * 16
          : settings.style === "zigzag"
            ? teeth * 2
            : Math.max(32, Math.round(length / (Math.min(w, h) / 95)));
      for (let i = 0; i <= count; i++) {
        const t = i / count;
        const noise = Math.sin((i + 1) * 127.1 + edge * 311.7) * 43758.5453;
        const d =
          settings.style === "wave"
            ? depth * (0.5 - 0.5 * Math.cos(t * teeth * Math.PI * 2))
            : settings.style === "zigzag"
              ? (i % 2) * depth
              : depth * (0.12 + 0.7 * (noise - Math.floor(noise)));
        const [x, y] = point(
          settings.style === "torn"
            ? depth + t * (length - 2 * depth)
            : t * length,
          d,
        );
        if (!edge && !i) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
    });
    ctx.closePath();
    ctx.fill();
  }
  const output = surface.getContext("2d");
  output.save();
  output.globalCompositeOperation = "destination-in";
  output.drawImage(mask, 0, 0);
  output.restore();
  return surface;
}
export {
  getImageBlurPixels,
  applyHueSaturationPixelFallback,
  applyImageBackgroundRemovalToCanvas,
  applyImageEdgesToCanvas,
  boxBlurImageData,
  supportsReliableCanvasBlurFilters,
};
